import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { checkVposReqBodyData, getBinListReqBodyData, onGetCommissionByBinNumberReqBodyData } from 'Config';
import { CheckVposPaymentModel, GetBinListModel, MasterpassCard } from 'Models';
import generateTLVTokenModel from 'Models/GenerateTLVToken';
import { extractMasterpassToken, isMasterpassVersion2 } from 'Services/Queries';
import { defaultTerminalGroupId, MasterpassVersion, toMasterpassVersion } from 'Utils/Masterpass';
import store, { RootState } from 'Stores';
import { fix3Dhtml } from 'Utils';
import { CreditCardTransactionsState, MasterPassTokenActionType, tempCard } from './types';

const initialState: CreditCardTransactionsState = {
  token: undefined,
  creditCardList: undefined,
  isConnection: false,
  loading: false,
  // ⚠️ Eski sabit `clientId: '34706580'` kaldırıldı — üye işyeri kimliği artık
  // JWT'nin `MerchantId` claim'inden okunup `Masterpass.setMerchantId()` ile kuruluyor.
  merchantId: undefined,
  // ⚠️ Ortama duyarlı: sabit yazıldığında canlıda TEST terminal grubu kullanılıyordu.
  mmid: defaultTerminalGroupId(),
  isHaveAccount: false,
  commissionAmount: undefined,
  loadAmount: 0,
  userTempCreditCard: undefined,
  tempMasterPassCard: undefined,
  activeSelectedCreditCard: undefined,
  vsPosBelbim3DsecureUrl: undefined,
  is3DSecureModalOpen: false,
  isUserAddedCreditCardWithMasterpass: true,
  creditCardBinList: undefined,
  activePayment: '',
  paymentStatus: false,
  masterPassAccountStatus: '',
  // Sürüm ilk token cevabıyla belirlenir; o ana kadar tanımsızdır.
  masterpassVersion: undefined,
  creditCardListAttemptedAt: undefined,
  isBinListRequesting: false,
  macroMerchantId: '',
};

const creditCardTransactions = createSlice({
  name: 'creditCardTransactions',
  initialState,
  reducers: {
    //get masterpass token
    onGetMasterpassTokenRequest: (state: CreditCardTransactionsState, { payload }: MasterPassTokenActionType) => {
      state.loading = true;
    },
    onGetMasterpassTokenRequestSuccess: (
      state: CreditCardTransactionsState,
      { payload }: PayloadAction<{ response: generateTLVTokenModel; isPaymentScoped: boolean; merchantId?: string }>
    ) => {
      // ⚠️ TOKEN İŞLEM KAPSAMINA BAĞLIDIR. Servis, gövdedeki `TokenType`'a göre
      // farklı kapsamda token üretiyor; ödeme tipleriyle (4/5/7) alınmış bir
      // token hesap işlemlerinde (kart ekleme/listeleme/silme) kullanılırsa
      // Masterpass `403 AUTHENTICATION_FAILED` döndürüyor.
      //
      // Bu yüzden token'ın kapsamı da saklanıyor; saga yalnızca AYNI kapsamdaki
      // token'ı yeniden kullanıyor.
      state.token = extractMasterpassToken(payload.response);
      state.tokenIsPaymentScoped = payload.isPaymentScoped;

      // ⚠️ Aktif sürümü BACKEND söyler. Uygulama yalnızca bu bayrağı okur;
      // istekte sürüm bilgisi taşınmaz.
      state.masterpassVersion = toMasterpassVersion(isMasterpassVersion2(payload.response));

      // ⚠️ Üye işyeri kimliği cevaptaki `MacroMerchantId`'den OKUNMAZ: o alan
      // v1'de terminal grubunu, v2'de üye işyerini taşıyor. Değer, sürümün kendi
      // kaynağından (v2'de JWT `MerchantId` claim'i) adaptörce çözülüp buraya
      // yazılıyor.
      state.merchantId = payload.merchantId || undefined;
      // Ödeme isteklerinde `terminalGroupId` alanına yazılan değer.
      state.macroMerchantId = payload.response.data.MacroMerchantId || '';
      state.loading = false;
    },
    onGetMasterpassTokenRequestFail: (state: CreditCardTransactionsState, { payload }: PayloadAction<string>) => {
      state.loading = false;
    },
    onGetMasterpassForMobileRequest: (state: CreditCardTransactionsState, { payload }: MasterPassTokenActionType) => {
      state.loading = true;
    },
    onGetMasterpassForMobileRequestSuccess: (state: CreditCardTransactionsState) => {
      state.loading = false;
    },
    onGetMasterpassForMobileRequestFail: (state: CreditCardTransactionsState, { payload }: PayloadAction<string>) => {
      state.loading = false;
    },
    onGetCreditCardListSuccess: (state: CreditCardTransactionsState, { payload }: PayloadAction<MasterpassCard[]>) => {
      state.creditCardList = payload;
      state.loading = false;
    },
    /**
     * Kart listesi çağrısı BEKLENMEDİK bir hatayla döndü (ağ, 5xx, yetki...).
     *
     * ⚠️ Liste BOŞALTILMAZ. Eskiden burada `creditCardList = []` yapılıyordu ve
     * geçici bir arıza kullanıcıya "hiç kartınız yok" gibi görünüyordu; kayıtlı
     * kartı olan kişi aynı kartı tekrar eklemeye çalışabiliyordu. Gerçek "kart
     * yok" durumu (`ACCOUNT_NOT_FOUND`) ayrı ele alınıyor ve listeyi
     * `onGetCreditCardListSuccess([])` ile boşaltıyor.
     */
    onGetCreditCardListFail: (state: CreditCardTransactionsState) => {
      state.loading = false;
    },

    //CHECK MASTERPASS
    onIsUserHaveAccount: (state: CreditCardTransactionsState, { payload }: PayloadAction<boolean>) => {
      state.isHaveAccount = payload;
    },
    //ADD TO CARD MASTERPASS
    onDeleteMasterPassRequest: (state: CreditCardTransactionsState) => {
      state.loading = true;
    },
    /**
     * Kart silindi.
     *
     * ⚠️ SİLİNEN KART LİSTEDEN HEMEN ÇIKARILIR — sunucudan gelecek tazelemeyi
     * beklemek YETMİYOR.
     *
     * Silme başarılı olunca saga `forceRefresh: true` ile kart listesini yeniden
     * istiyor, hemen ardından da `goBack()` ile listeleme ekranına dönüyor. O
     * ekran açılışta kendi kart listesi isteğini atıyor ve watcher `takeLatest`
     * kullandığı için bu ikinci istek, henüz tamamlanmamış ZORUNLU TAZELEMEYİ
     * İPTAL EDİYOR. İkinci istek `forceRefresh` taşımadığından, bellekteki
     * (bayat) liste dolu göründüğü için kısa devre yapıyor ve hiç istek atmıyor.
     * Sonuç: silinen kart ekranda kalıyor, tekrar silinmeye çalışılınca
     * "bulunamadı" hatası alınıyor.
     *
     * Listeyi burada güncelleyerek sonuç yarışın kim kazandığından bağımsız
     * hale geliyor: ekran anında doğru, sunucu tazelemesi de geldiğinde üstüne
     * yazıyor.
     */
    onDeleteMasterPassSuccess: (
      state: CreditCardTransactionsState,
      { payload }: PayloadAction<string | undefined>
    ) => {
      state.loading = false;

      if (payload && state.creditCardList) {
        state.creditCardList = state.creditCardList.filter((card) => card.cardAlias !== payload);
      }
    },
    onDeleteMasterPassFail: (state: CreditCardTransactionsState) => {
      state.loading = false;
    },
    //ADD TO CARD MASTERPASS OTP
    onAddCardOTPRequest: (state: CreditCardTransactionsState, { payload }: PayloadAction<string>) => {
      state.loading = true;
    },
    onAddCardOtpSuccess: (state: CreditCardTransactionsState) => {
      state.loading = false;
    },
    onAddCardOtpFail: (state: CreditCardTransactionsState, { payload }: PayloadAction<string>) => {
      state.loading = false;
    },
    onGetCommissionByBinNumberRequest: (
      state: CreditCardTransactionsState,
      { payload }: PayloadAction<onGetCommissionByBinNumberReqBodyData>
    ) => {
      state.loading = true;
    },
    onGetCommissionByBinNumberRequestSuccess: (state: CreditCardTransactionsState, { payload }: PayloadAction<any>) => {
      state.loading = false;
      state.commissionAmount = payload;
    },
    onGetCommissionByBinNumberRequestFail: (state: CreditCardTransactionsState, { payload }: PayloadAction<string>) => {
      state.loading = false;
      state.error = payload;
    },
    resetCommissionAmount: (state: CreditCardTransactionsState) => {
      state.commissionAmount = undefined;
    },
    setUserTemporaryCreditCard: (state: CreditCardTransactionsState, { payload }: PayloadAction<any>) => {
      state.userTempCreditCard = payload;
    },

    vsPosBelbimPaymentRequest: (state: CreditCardTransactionsState, { payload }: PayloadAction<any>) => {
      state.loading = true;
    },
    vsPosBelbimPaymentRequestSuccess: (state: CreditCardTransactionsState, { payload }: PayloadAction<any>) => {
      /* Belbim banka ödemelerinin geri dönüşleri stabil olmadığı için böyle bir
       * yol izlemek zorunda kalındı.
       */
      // 3D biterken ne yapılacağını belirler — bkz. `threeDSecureFlow`.
      state.threeDSecureFlow = 'payment';
      try {
        const json = fix3Dhtml(payload);
        state.loading = false;
        state.vsPosBelbim3DsecureUrl = json.url;
        state.activePayment = json.PaymentNumber;
      } catch (error) {
        state.vsPosBelbim3DsecureUrl = payload.url;
        state.activePayment = payload.PaymentNumber;
        state.loading = false;
      }
    },
    vsPosBelbimPaymentRequestFail: (state: CreditCardTransactionsState, { payload }: PayloadAction<string>) => {
      state.loading = false;
      state.error = payload;
    },
    setActiveSelectedCreditCard: (state: CreditCardTransactionsState, { payload }: PayloadAction<any>) => {
    
      state.activeSelectedCreditCard = payload;
    },
    setLoadAmount: (state: CreditCardTransactionsState, { payload }: PayloadAction<number>) => {
      state.loadAmount = payload;
    },
    open3DSecureModal: (state: CreditCardTransactionsState) => {
      state.is3DSecureModalOpen = true;
    },
    close3DSecureModal: (state: CreditCardTransactionsState) => {
      state.is3DSecureModalOpen = false;
    },
    /**
     * 3D/ACS sayfasının adresini doğrudan set eder.
     *
     * Ödeme akışı bu adresi `vsPosBelbimPaymentRequestSuccess` içinden, HTML
     * cevabını ayrıştırarak kuruyor. Kart kaydında ise adres SDK cevabında hazır
     * geliyor (`url3d`), ayrıştırma gerekmiyor — bu yüzden ayrı bir action.
     */
    set3DSecureUrl: (state: CreditCardTransactionsState, { payload }: PayloadAction<string>) => {
      state.vsPosBelbim3DsecureUrl = payload;
      // 3D biterken ne yapılacağını belirler — bkz. `threeDSecureFlow`.
      state.threeDSecureFlow = 'cardRegistration';
    },
    /** Ödeme 3D'sinin hangi servisle başlatıldığını işaretler — bkz. `threeDSecureFlow`. */
    setThreeDSecureFlow: (
      state: CreditCardTransactionsState,
      { payload }: PayloadAction<'payment' | 'directPayment' | 'cardRegistration'>
    ) => {
      state.threeDSecureFlow = payload;
    },
    /**
     * Açık OTP ekranının hangi işleme ait olduğunu işaretler — bkz. `pendingOtpFlow`.
     *
     * ⚠️ `undefined` KABUL EDER VE ETMELİDİR. Bayrak bir dönem yalnızca talimat
     * dalında yazılıyor, hiçbir yerde temizlenmiyordu; `'cardRegistration'` ise
     * hiç dispatch edilmiyordu. Sonuç: talimat kurmayı yarım bırakan kullanıcı
     * aynı oturumda kart eklediğinde, OTP doğrulaması başarılı dönmesine rağmen
     * akış talimat bitişine sapıyordu — kart kaydedilir ama liste tazelenmez,
     * seçili kart oluşmaz, kullanıcı yanlış ekrana düşerdi.
     * Her akış artık kendi değerini AÇIKÇA yazıyor ve bitişte temizliyor.
     */
    setPendingOtpFlow: (
      state: CreditCardTransactionsState,
      { payload }: PayloadAction<'cardRegistration' | 'recurringOrder' | undefined>
    ) => {
      state.pendingOtpFlow = payload;
    },
    /**
     * Kayıtlı kart listesini SUNUCUDAN tazele.
     *
     * ⚠️ NEDEN AYRI BİR ACTION: liste tazeleme istekleri `onGetMasterpassTokenRequest`
     * ile gönderildiğinde, watcher `takeLatest` kullandığı için ekran geçişindeki
     * herhangi bir token isteği tarafından İPTAL EDİLİYORDU. Tipik zincir: kart
     * eklendi → zorunlu tazeleme dispatch edildi → hemen ardından navigasyon →
     * varış ekranı mount'ta kendi token isteğini attı → tazeleme iptal oldu →
     * yeni istek bayat ama dolu listeyi görüp kısa devre yaptı → liste hiç
     * güncellenmedi.
     *
     * Ayrı action tipi olduğu için kendi watcher'ı var ve token isteklerinin
     * `takeLatest`'i bunu iptal edemez. Ekranlara tek tek `forceRefresh`
     * serpiştirme ihtiyacını da ortadan kaldırır.
     */
    refreshCreditCardListRequest: (state: CreditCardTransactionsState) => {
      state.loading = true;
    },
    /**
     * Masterpass sürümünü DOĞRUDAN yazar — token cevabı beklemeden.
     *
     * ⚠️ YALNIZCA MOBİL WEBVIEW AKIŞLARI İÇİN. Web'de sürüm token cevabındaki
     * `IsVersion2Active`'ten gelir; mobil ise token'ı native taraftan hazır alır
     * ve token servisini hiç çağırmaz, dolayısıyla o cevabı hiç görmez. Native
     * bayrağı WebView adresinde iletiyor ve ilgili ekran açılışta bir kez buraya
     * yazıyor.
     *
     * ⚠️ NEDEN PAYLOAD'A DEĞİL STORE'A: `onGetMasterpassForMobileRequest` 8
     * dosyada 16 ayrı yerden dispatch ediliyor ve bunların bir kısmı OTP / tekrar
     * gönderim gibi AKIŞIN ORTASINDAKİ adımlar. Bayrağı payload'a koymak, o 16
     * noktanın hepsini doğru geçirmeye bağlı olurdu; biri unutulduğunda akış
     * ortasında sürüm değişir. Store'a bir kez yazmak zinciri baştan sona kapatır.
     */
    setMasterpassVersion: (
      state: CreditCardTransactionsState,
      { payload }: PayloadAction<MasterpassVersion>
    ) => {
      state.masterpassVersion = payload;
    },
    /**
     * Talimat kaydı TAMAMLANDI sinyali — saga tarafından yakalanır.
     * `cardRegistrationCompleted`'in talimat karşılığı: bitişte yapılacak iş farklı
     * (liste tazeleme/kart aktifleştirme yok, başarı adımına geçiş var).
     */
    recurringOrderCompleted: (state: CreditCardTransactionsState) => {},
    /**
     * Kart kaydı TAMAMLANDI sinyali — saga tarafından yakalanır (state'i değiştirmez).
     *
     * İki yerden tetiklenir: OTP doğrulaması başarılı olduğunda (saga) ve ACS
     * sayfasıyla tamamlanan kayıtta kart listede göründüğünde (AddCreditCard).
     * Ortak son adımların (liste tazeleme, geçici kartı aktif etme, akışa göre
     * yönlendirme) tek yerde durması için action'a bağlandı.
     */
    cardRegistrationCompleted: (state: CreditCardTransactionsState) => {},
    /**
     * 3D akışı bitti sinyali — `public/masterpass-3d-return.html` sayfasından
     * `postMessage` ile gelir, `Modal3DSecure` dinleyip dispatch eder.
     *
     * ⚠️ Bu "ödeme tamamlandı" DEMEK DEĞİLDİR. Masterpass 3D'yi başarıyla
     * bitirdiğinde işlem yalnızca YETKİLENDİRİLMİŞ olur (`paymentReceived: 0`) ve
     * gövdede "ödeme tamamlama servisi (Complete Payment) çağırınız" der.
     * Tahsilat, saga'nın bu action üzerine yaptığı `completeRegistration`
     * (→ `/account/api/PurchaseAndRegister`) çağrısıyla gerçekleşir.
     */
    masterpass3DFlowCompleted: (
      state: CreditCardTransactionsState,
      { payload }: PayloadAction<{ responseCode: string; token?: string }>
    ) => {},
    setIsUserAddedCreditCardWithMasterpass: (
      state: CreditCardTransactionsState,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isUserAddedCreditCardWithMasterpass = payload;
    },
    getBinListRequest: (state: CreditCardTransactionsState, { payload }: PayloadAction<getBinListReqBodyData>) => {
      state.loading = true;
      state.isBinListRequesting = true;
    },
    getBinListRequestSuccess: (state: CreditCardTransactionsState, { payload }: PayloadAction<GetBinListModel>) => {
      state.loading = false;
      state.creditCardBinList = payload.data.BinList;
      state.isBinListRequesting = false;
    },
    getBinListRequestFail: (state: CreditCardTransactionsState, { payload }: PayloadAction<string>) => {
      state.loading = false;
      state.error = payload;
      state.isBinListRequesting = false;
    },
    checkPaymentStatusRequest: (
      state: CreditCardTransactionsState,
      { payload }: PayloadAction<checkVposReqBodyData>
    ) => {},
    checkPaymentStatusRequestResult: (
      state: CreditCardTransactionsState,
      { payload }: PayloadAction<CheckVposPaymentModel>
    ) => {
      const status = payload.data.IsSuccess;
      state.paymentStatus = status ? status : !state.paymentStatus;
      state.activePayment = status ? undefined : state.activePayment;
    },

    setMasterPassAccountStatus: (state: CreditCardTransactionsState, { payload }: PayloadAction<string>) => {
      state.masterPassAccountStatus = payload;
    },
    /** Kart listesi servisten istendiği anda işaretlenir (başarılı olsun ya da olmasın). */
    markCreditCardListAttempted: (state: CreditCardTransactionsState) => {
      state.creditCardListAttemptedAt = Date.now();
    },
    setCreditCardList: (state: CreditCardTransactionsState, { payload }: PayloadAction<undefined>) => {
      state.creditCardList = payload;
    },
    setLoadingForMasterpass: (state: CreditCardTransactionsState, { payload }: PayloadAction<boolean>) => {
      state.loading = payload;
    },
    setTempMasterPassCard: (state: CreditCardTransactionsState, { payload }: PayloadAction<tempCard>) => {

      state.tempMasterPassCard = payload;
    },
    /**
     * Yeni eklenen kartı aktif seçili kart yapar.
     *
     * ⚠️ GEÇİCİ KART YOKSA DOKUNMAZ. Bu action `handleCardRegistrationCompleted`
     * içinden çağrılıyor ve o handler İKİ akış için ortak: kart ekleme (REGISTER)
     * ve hesap bağlama (LINK). `tempMasterPassCard` yalnızca REGISTER dalında
     * doldurulur (sagas.ts, `setTempMasterPassCard`); bağlama akışında undefined'dır.
     *
     * Koşulsuz atama yapıldığında bağlama sonrası `activeSelectedCreditCard`
     * undefined'a EZİLİYOR, kullanıcı dolu listeye rağmen boş bir "ödeme aracı"
     * kutusu görüyordu. Liste efektindeki varsayılan seçim de tekrar çalışmıyor
     * çünkü `creditCardList` değişmemiş oluyor.
     */
    setTempMasterPassCardActive: (state: CreditCardTransactionsState) => {
      if (!state.tempMasterPassCard) {
        return;
      }

      state.activeSelectedCreditCard = state.tempMasterPassCard;
      state.userTempCreditCard = state.tempMasterPassCard;
    },
    resetCreditCardTransactionStore: (state: CreditCardTransactionsState) => initialState,
  },
});
export const creditCardTransactionsStore = (state: RootState) => state.creditCardTransactions;

export const creditCardIsLoading = (state: RootState) => state.creditCardTransactions.loading;

export const {
  onGetMasterpassTokenRequest,
  onGetMasterpassTokenRequestSuccess,
  onGetMasterpassTokenRequestFail,
  onDeleteMasterPassRequest,
  onDeleteMasterPassSuccess,
  onDeleteMasterPassFail,
  onAddCardOTPRequest,
  onAddCardOtpSuccess,
  onAddCardOtpFail,
  onGetCommissionByBinNumberRequest,
  onGetCommissionByBinNumberRequestSuccess,
  onGetCommissionByBinNumberRequestFail,
  resetCommissionAmount,
  onGetCreditCardListSuccess,
  onGetCreditCardListFail,
  setUserTemporaryCreditCard,
  setActiveSelectedCreditCard,
  setLoadAmount,
  vsPosBelbimPaymentRequest,
  vsPosBelbimPaymentRequestSuccess,
  vsPosBelbimPaymentRequestFail,
  open3DSecureModal,
  close3DSecureModal,
  set3DSecureUrl,
  setThreeDSecureFlow,
  setPendingOtpFlow,
  refreshCreditCardListRequest,
  setMasterpassVersion,
  recurringOrderCompleted,
  cardRegistrationCompleted,
  masterpass3DFlowCompleted,
  setIsUserAddedCreditCardWithMasterpass,
  getBinListRequest,
  getBinListRequestSuccess,
  getBinListRequestFail,
  checkPaymentStatusRequest,
  checkPaymentStatusRequestResult,
  setMasterPassAccountStatus,
  setCreditCardList,
  markCreditCardListAttempted,
  setLoadingForMasterpass,
  onGetMasterpassForMobileRequest,
  onGetMasterpassForMobileRequestSuccess,
  onGetMasterpassForMobileRequestFail,
  resetCreditCardTransactionStore,
  setTempMasterPassCard,
  setTempMasterPassCardActive,
} = creditCardTransactions.actions;
export default creditCardTransactions.reducer;
